// File path: src/constants/coinglass.ts

import type { DataType, DataTypeConfig, TimeRange } from "@/types/coinglass";

/**
 * Constants for Coinglass components (FundingRate, WeightedFundingRate, etc.)
 */

// ============================================================================
// EXCHANGES
// ============================================================================

/**
 * Supported exchanges for funding rate data
 */
export const EXCHANGES = [
  "binance",
  "bybit",
  "bitget",
  "okx",
  "hyperliquid",
] as const;

export type ExchangeName = (typeof EXCHANGES)[number];

// ============================================================================
// TIME RANGES
// ============================================================================

/**
 * Time range configurations with millisecond values
 */
export const TIME_RANGES: Record<TimeRange, number> = {
  "1D": 24 * 60 * 60 * 1000,
  "7D": 7 * 24 * 60 * 60 * 1000,
  "1M": 30 * 24 * 60 * 60 * 1000,
  "3M": 90 * 24 * 60 * 60 * 1000,
  ALL: Infinity,
} as const;

/**
 * Aggregation levels based on data point count
 * Determines how many points to combine for performance
 */
export const AGGREGATION_LEVELS = {
  NONE: 1,
  LOW: 2,
  MEDIUM: 4,
  HIGH: 8,
  VERY_HIGH: 16,
} as const;

// ============================================================================
// CHART STYLING
// ============================================================================

/**
 * Color palette for exchange lines in the chart
 */
export const EXCHANGE_COLORS: Record<string, string> = {
  binance: "#F3BA2F",
  bybit: "#F7A600",
  bitget: "#00C087",
  okx: "#000000",
  hyperliquid: "#7C3AED",
} as const;

/**
 * Price line styling
 */
export const PRICE_LINE_COLOR = "#8B5CF6";
export const PRICE_LINE_WIDTH = 2;
export const PRICE_LINE_OPACITY = 0.6;

/**
 * Funding rate line styling
 */
export const FUNDING_RATE_LINE_WIDTH = 1.5;

// ============================================================================
// CHART CONFIGURATION
// ============================================================================

/**
 * Threshold for determining when to show data aggregation
 * If visible points exceed this, aggregation is applied
 */
export const AGGREGATION_THRESHOLD = 1000;

/**
 * Animation duration for chart updates (in milliseconds)
 */
export const CHART_ANIMATION_DURATION = 300;

/**
 * Grid styling
 */
export const GRID_STYLING = {
  left: "3%",
  right: "3%",
  bottom: "15%",
  top: "15%",
  containLabel: true,
} as const;

// ============================================================================
// DATA FETCHING
// ============================================================================

/**
 * React Query configuration for Coinglass data
 */
export const COINGLASS_QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  refetchOnWindowFocus: false,
  retry: 2,
} as const;

/**
 * Interval for real-time updates (if needed)
 */
export const REFETCH_INTERVAL = 60 * 1000; // 1 minute



// WEIGHTED

/**
 * Data type configuration mapping
 * Maps each data type to its API endpoint and display metadata
 */
export const DATA_TYPE_CONFIG: Record<DataType, DataTypeConfig> = {
  oiw: {
    endpoint: "/coinglass/history/fr-oiw",
    title: "OI-Weighted Funding Rate",
    description: "Funding rate weighted by open interest across exchanges",
    labelShort: "OI-Weighted",
  },
  volw: {
    endpoint: "/coinglass/history/fr-volw",
    title: "Volume-Weighted Funding Rate",
    description: "Funding rate weighted by trading volume across exchanges",
    labelShort: "Vol-Weighted",
  },
};
